/* Homepage direct-order enhancement. Native Shopify form is the fallback.
 * The cart is never cleared; precisely one selected P1-dongle is added.
 */
(() => {
 'use strict';
 if (!window.customElements || customElements.get('zv-p1-home-order')) return;
function createP1Commerce(root, { signal } = {}) {
  const form = root.querySelector('.p1-product-form');
  if (!form) return { syncAvailability() {}, reset() {}, destroy() {} };

  const buttons = Array.from(root.querySelectorAll('[data-p1-buy]'))
    .filter((button) => button.form === form);
  const variant = form.querySelector('[name="id"]');
  const initialAvailability = buttons.some((button) => !button.disabled);
  const variantWasDisabled = Boolean(variant?.disabled);
  const originalLabels = new Map(buttons.map((button) => [button,
    button.dataset.p1Label || button.querySelector('[data-p1-buy-label]')?.textContent.trim() ||
    (button.dataset.p1PurchaseAction === 'checkout' ? window.ZinVoltI18n.t("buy_now_b06a19") : window.ZinVoltI18n.t("add_to_cart_646feb"))
  ]));
  const shopRoot = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
  const cartURL = window.routes?.cart_url || `${shopRoot}cart`;
  const addURL = `${shopRoot}cart/add.js`;
  const checkoutURL = `${shopRoot}checkout`;
  let busy = false;
  let activeAction = null;
  let requestController = null;
  let requestNumber = 0;
  let destroyed = false;
  let status = root.querySelector('[data-p1-commerce-status]');

  if (!status) {
    status = document.createElement('p');
    status.className = 'p1-commerce-status';
    status.dataset.p1CommerceStatus = '';
    status.hidden = true;
    status.tabIndex = -1;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    form.insertAdjacentElement('afterend', status);
  }

  function available() {
    if (!variant?.value || !/^[1-9]\d*$/.test(variant.value)) return false;
    const selected = variant.selectedOptions?.[0];
    return selected ? selected.dataset.available === 'true' : initialAvailability;
  }

  function syncAvailability() {
    const inStock = available();
    buttons.forEach((button) => {
      button.disabled = busy || !inStock;
      button.setAttribute('aria-disabled', String(button.disabled));
      button.toggleAttribute('aria-busy', busy);
      const label = button.querySelector('[data-p1-buy-label]');
      if (label) {
        label.textContent = !inStock ? window.ZinVoltI18n.t("temporarily_sold_out_8564f7") : busy
          ? (activeAction === 'checkout' ? window.ZinVoltI18n.t("going_to_checkout_671b53") : window.ZinVoltI18n.t("adding_774ee6"))
          : originalLabels.get(button);
      }
    });
  }

  function setBusy(value, action = null) {
    busy = value;
    activeAction = action;
    form.toggleAttribute('aria-busy', value);
    root.toggleAttribute('data-p1-purchasing', value);
    if (variant?.tagName === 'SELECT') variant.disabled = value || variantWasDisabled;
    syncAvailability();
  }

  function message(text, { error = false, link = null, focus = false } = {}) {
    status.replaceChildren(document.createTextNode(text));
    status.dataset.state = error ? 'error' : 'success';
    if (link) {
      const anchor = document.createElement('a');
      anchor.href = link.href;
      anchor.textContent = link.label;
      anchor.className = 'zv-text-link';
      status.append(document.createTextNode(' '), anchor);
    }
    status.hidden = !text;
    if (focus) {
      status.focus({ preventScroll: true });
      status.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }

  function reset() {
    requestNumber += 1;
    requestController?.abort();
    requestController = null;
    setBusy(false);
  }

  async function submit(event) {
    if (event.defaultPrevented) return;
    if (!form.checkValidity()) return;
    // If enhancement is unavailable, retain the native form and its return_to.
    if (typeof fetch !== 'function' || typeof FormData !== 'function') return;
    event.preventDefault();
    if (busy || !available() || destroyed) return;

    const submitter = event.submitter;
    const action = submitter?.dataset.p1PurchaseAction === 'checkout' ? 'checkout' : 'cart';
    const payload = new FormData(form);
    payload.set('quantity', '1');
    payload.delete('return_to');
    const drawer = action === 'cart' ? document.querySelector('cart-drawer') : null;
    let sectionIDs = [];
    if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
      sectionIDs = drawer.getSectionsToRender().map((section) => section.id).filter(Boolean).slice(0, 5);
      if (sectionIDs.length) {
        payload.set('sections', sectionIDs.join(','));
        payload.set('sections_url', window.location.pathname);
      }
      drawer.setActiveElement?.(submitter || document.activeElement);
    }

    const thisRequest = ++requestNumber;
    const controller = new AbortController();
    requestController = controller;
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    let added = false;
    let leaving = false;
    setBusy(true, action);
    message('');

    try {
      const response = await fetch(addURL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: payload,
        signal: controller.signal
      });
      const result = await response.json().catch(() => null);
      if (thisRequest !== requestNumber || destroyed) return;

      // A 400 or network failure can still follow a successful cart mutation.
      // Never automatically repeat the POST or claim that the cart is unchanged.
      if (!response.ok || !result || result.status) {
        const error = new Error('Cart addition was not confirmed');
        error.customerMessage = response.status === 422 && typeof result?.description === 'string'
          ? result.description
          : window.ZinVoltI18n.t("we_could_not_confirm_that_the_item_f3f52e");
        throw error;
      }
      added = true;

      if (action === 'checkout') {
        // Add only; keep other line items, notes, discounts and cart attributes.
        // The customer goes directly to checkout without visiting the cart page.
        window.location.assign(checkoutURL);
        leaving = true;
        return;
      }

      const completeDrawer = sectionIDs.length > 0 && sectionIDs.every((id) =>
        typeof result.sections?.[id] === 'string' && result.sections[id].trim()
      );
      if (drawer && completeDrawer) {
        try {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
          message(window.ZinVoltI18n.t("the_p1_dongle_pro_is_in_your_78eea1"));
          return;
        } catch (_) {
          // The item is already added. Navigate to the cart, never add again.
        }
      }
      window.location.assign(cartURL);
      leaving = true;
    } catch (error) {
      if (thisRequest !== requestNumber || destroyed) return;
      if (added) {
        message(window.ZinVoltI18n.t("the_p1_dongle_pro_has_been_added_94b399"), {
          link: { href: action === 'checkout' ? checkoutURL : cartURL,
            label: action === 'checkout' ? window.ZinVoltI18n.t("go_to_checkout_55a15b") : window.ZinVoltI18n.t("view_your_cart_d6f557") },
          focus: true
        });
      } else {
        message(error.customerMessage || window.ZinVoltI18n.t("we_could_not_confirm_that_the_item_f3f52e"), {
          error: true,
          link: { href: cartURL, label: window.ZinVoltI18n.t("view_your_cart_d6f557") },
          focus: true
        });
      }
    } finally {
      window.clearTimeout(timeout);
      if (thisRequest === requestNumber) {
        requestController = null;
        // Keep all order buttons locked until navigation; pageshow restores them.
        if (!leaving && !destroyed) setBusy(false);
      }
    }
  }

  function onPageShow(event) {
    if (event.persisted) reset();
  }

  function destroy() {
    destroyed = true;
    reset();
    form.removeEventListener('submit', submit);
    window.removeEventListener('pageshow', onPageShow);
  }

  form.addEventListener('submit', submit);
  window.addEventListener('pageshow', onPageShow);
  signal?.addEventListener('abort', destroy, { once: true });
  syncAvailability();
  return { syncAvailability, reset, destroy };
}
 class ZinVoltP1HomeOrder extends HTMLElement {
  connectedCallback() {
   if (this.controller) return;
   this.controller = new AbortController();
   this.commerce = createP1Commerce(this, {signal:this.controller.signal});
  }
  disconnectedCallback() {
   this.controller?.abort();
   this.controller = null;
   this.commerce = null;
  }
 }
 customElements.define('zv-p1-home-order', ZinVoltP1HomeOrder);
})();
